/**
 * Roles and visibility. The rules here are consumed by the API to build the
 * WHERE clause on every query, so a member cannot receive another member's
 * records no matter what the request looks like. The interface never decides
 * visibility on its own.
 */

export type OrgRole = 'owner' | 'admin' | 'manager' | 'agent' | 'compliance';
export type TeamRole = 'leader' | 'member';

export const ORG_ROLES: readonly OrgRole[] = ['owner', 'admin', 'manager', 'agent', 'compliance'];

/**
 * What each role is called on screen.
 *
 * "Agency owner", not "Owner". The word on its own is ambiguous in a platform
 * that has agencies on it: the person who runs Loyalty Life and the person who
 * owns Cellvora are not the same person, and a row that says only "Owner" reads
 * as the second when it means the first. Every role here is a role *inside one
 * agency* and the labels say so.
 *
 * There is no platform-owner role and there should not be one. Whoever runs
 * Cellvora is an owner of their own agency like anybody else; the extra powers
 * that come with running the platform are held by `SECRET_KEY` and the Netlify
 * account, not by a row in `users`.
 *
 * Labels live here rather than in the screens so a role cannot be called one
 * thing in Settings and another on the onboarding form.
 */
export const ORG_ROLE_LABEL: Record<OrgRole, string> = {
  owner: 'Agency owner',
  admin: 'Administrator',
  manager: 'Manager',
  agent: 'Agent',
  compliance: 'Compliance',
};

/** One line on what the role can actually do, in the words the floor uses. */
export const ORG_ROLE_BLURB: Record<OrgRole, string> = {
  owner: 'Runs this agency. Every lead, every case, every person, every setting.',
  admin: 'Everything in this agency except making somebody else an agency owner.',
  manager: 'Their teams — those leads, imports, and who is on the team.',
  agent: 'Their own leads and their own cases. Nobody else\u2019s.',
  /*
   * This said "Reads everything, changes nothing." It reads everything and
   * changes three things: the do-not-call list (`dnc.write`) and the calling
   * rules (`config.compliance` — window, attempt cap, consent requirement).
   * Those are exactly the changes the role exists to make; a compliance officer
   * who cannot add a number to the DNC list or tighten a calling window is an
   * observer, not an officer. So the fix is to name them rather than remove
   * them. A blurb that undersells its own write access is how somebody is
   * handed the role on the understanding it is read-only.
   */
  compliance: 'Reads everything. Changes only the do-not-call list and the calling rules.',
};

/**
 * Every capability there is, as a list, with the type derived from it.
 *
 * It is a list rather than a hand-written union so that anywhere needing "all
 * of them" can be handed this instead of retyping it. That is not tidiness.
 * The API tells each caller which capabilities they hold and the interface
 * decides what to show from that answer, so a capability missing from a second,
 * hand-maintained copy of this list is a control that silently does not exist
 * for anybody — including the owner, who holds every capability and would still
 * be shown the screen without the button on it. That is precisely what had
 * happened to `role.assign`: the API never mentioned it, so the role dropdown
 * in Settings → People was inert for every user in the agency.
 */
export const CAPABILITIES = [
  'org.manage', 'org.billing', 'user.invite', 'user.manage', 'role.assign',
  'team.create', 'team.manage.any', 'team.manage.own', 'team.join.approve',
  'lead.read.own', 'lead.read.team', 'lead.read.all',
  'lead.write.own', 'lead.write.team', 'lead.write.all',
  'lead.import', 'lead.export', 'lead.assign',
  'config.scoring', 'config.compliance', 'config.routing', 'config.production',
  'dnc.read', 'dnc.write', 'consent.read',
  'audit.read', 'report.run', 'ai.use',
  /*
   * Registering the agency's telephone numbers, pointing them at an advert and
   * at a person, and writing the greeting a customer hears.
   *
   * A ROLE QUESTION, unlike working the line itself. Where an agency's calls go
   * is a decision about the business; whether a particular agent is on the
   * inbound rota this week is not, and no role expresses it. That one is
   * `users.inboundAccess`, a per-person grant — see migration 0030.
   */
  'inbound.manage',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

const MATRIX: Record<OrgRole, Capability[]> = {
  owner: [
    'org.manage', 'org.billing', 'user.invite', 'user.manage', 'role.assign',
    'team.create', 'team.manage.any', 'team.join.approve',
    'lead.read.all', 'lead.write.all', 'lead.import', 'lead.export', 'lead.assign',
    'config.scoring', 'config.compliance', 'config.routing', 'config.production',
    'dnc.read', 'dnc.write', 'consent.read', 'audit.read', 'report.run', 'ai.use',
    'inbound.manage',
  ],
  admin: [
    'org.manage', 'user.invite', 'user.manage', 'role.assign',
    'team.create', 'team.manage.any', 'team.join.approve',
    'lead.read.all', 'lead.write.all', 'lead.import', 'lead.export', 'lead.assign',
    'config.scoring', 'config.compliance', 'config.routing', 'config.production',
    'dnc.read', 'dnc.write', 'consent.read', 'audit.read', 'report.run', 'ai.use',
    'inbound.manage',
  ],
  manager: [
    'user.invite', 'team.create', 'team.manage.own', 'team.join.approve',
    'lead.read.team', 'lead.write.team', 'lead.import', 'lead.export', 'lead.assign',
    'config.scoring', 'config.routing',
    'dnc.read', 'dnc.write', 'consent.read', 'report.run', 'ai.use',
    'inbound.manage',
  ],
  agent: [
    'lead.read.own', 'lead.write.own', 'dnc.read', 'dnc.write', 'ai.use',
  ],
  compliance: [
    'lead.read.all', 'dnc.read', 'dnc.write', 'consent.read',
    'audit.read', 'report.run', 'config.compliance',
  ],
};

export function can(role: OrgRole, capability: Capability, opts?: { isTeamLeader?: boolean }): boolean {
  if (MATRIX[role].includes(capability)) return true;
  // A team leader gains team-wide read/write and approval over their own teams,
  // whatever their organisation role is.
  if (opts?.isTeamLeader) {
    const leaderGrants: Capability[] = [
      'lead.read.team', 'lead.write.team', 'lead.assign',
      'team.manage.own', 'team.join.approve', 'report.run',
    ];
    if (leaderGrants.includes(capability)) return true;
  }
  return false;
}

export type LeadScope =
  | { kind: 'all' }
  | { kind: 'teams'; teamIds: string[]; userId: string }
  | { kind: 'own'; userId: string }
  | { kind: 'none' };

export interface ScopeInput {
  role: OrgRole;
  userId: string;
  /** Teams where this user is the leader. */
  ledTeamIds: string[];
  /** Teams this user belongs to at all. */
  memberTeamIds: string[];
}

/**
 * The single function that decides which leads a person may see.
 *
 * - owner / admin / compliance: everything in the organisation
 * - manager: their own book plus every team they lead
 * - team leader (any role): their own book plus the teams they lead
 * - agent: their own book only — never another agent's, even inside their team
 */
export function leadScope(input: ScopeInput): LeadScope {
  if (input.role === 'owner' || input.role === 'admin' || input.role === 'compliance') {
    return { kind: 'all' };
  }
  if (input.ledTeamIds.length > 0) {
    return { kind: 'teams', teamIds: [...input.ledTeamIds], userId: input.userId };
  }
  if (input.role === 'manager') {
    // A manager with no team yet still sees only their own book.
    return { kind: 'own', userId: input.userId };
  }
  return { kind: 'own', userId: input.userId };
}

/** True when `viewer` may open the profile and metrics of `target`. */
export function canSeeMember(
  viewer: ScopeInput, target: { userId: string; teamIds: string[] },
): boolean {
  if (viewer.role === 'owner' || viewer.role === 'admin' || viewer.role === 'compliance') return true;
  if (viewer.userId === target.userId) return true;
  return target.teamIds.some((t) => viewer.ledTeamIds.includes(t));
}
